import { AggregatedInboundRow, Amount, PartnerSettlementStatisticItem } from '@/services/settlement/settlementManagement/types'

/**
 * LP8238 박스단위 입고 정산 — INBOUND raw N row 집계.
 *
 * BE 는 product+date 당 BOX row + PCS row 를 raw N row 로 내려준다(BE FR-AP01).
 * 화면은 서버의 productAxis와 정산일에 따라 SKU 또는 바코드로 묶는다 (LOGISPRD-15152).
 *
 * - `unitType === 'BOX'` → boxQuantity / pcsPerUnit / boxUnitPrice
 * - 그 외(`unitType === 'PCS'` 또는 `unitType` 누락 legacy) → `else` 분기로 PCS 처리 (NFR01 / AC-04)
 * - 입고비 totalAmount = BOX row + PCS row 금액 합
 * - 총수량 = (pcsQuantity ?? 0) + (boxQuantity ?? 0) × (pcsPerUnit ?? 0) (PCS 환산)
 *
 * `Map` 은 삽입 순서를 보존하므로 BE 가 그룹 단위로 페이징해 보낸 순서를 그대로 유지한다(§3.5).
 */
export function groupInboundSettlementDetails(rows: PartnerSettlementStatisticItem[]): AggregatedInboundRow[] {
  const groups = new Map<string, AggregatedInboundRow>()

  rows.forEach((row) => {
    const useSku = row.productAxis === 'SKU' && row.skuId != null && row.skuId !== ''
    const productAxis = useSku ? 'SKU' : 'BARCODE'
    const key = JSON.stringify([row.settlementDate, productAxis, useSku ? row.skuId : row.productBarcode])
    const group: AggregatedInboundRow = groups.get(key) ?? {
      settlementDate: row.settlementDate,
      brandId: row.brandId,
      brandName: row.brandName,
      productNumber: row.productNumber,
      productName: row.productName,
      productOption: row.productOption,
      productBarcode: row.productBarcode,
      skuId: row.skuId,
      productAxis,
      totalAmount: { originAmount: 0, discountedAmount: 0 },
      totalQuantity: 0,
    }
    groups.set(key, group)

    // 입고비는 BOX/PCS row 금액을 합산한다.
    group.totalAmount = {
      originAmount: group.totalAmount.originAmount + (row.totalAmount?.originAmount ?? 0),
      discountedAmount: group.totalAmount.discountedAmount + (row.totalAmount?.discountedAmount ?? 0),
    }

    const unitPrice: Amount = { originAmount: row.unitPrice, discountedAmount: row.discountedUnitPrice }
    // 수량은 누적한다. BE 계약상 그룹당 BOX/PCS row 는 각 1개지만, 만약 동일 unitType row 가 중복돼도
    // 총수량(=수량 합)과 입고비(=금액 합)가 일관되도록 방어한다 (덮어쓰기 시 수량 누락 방지).
    if (row.unitType === 'BOX') {
      group.boxQuantity = (group.boxQuantity ?? 0) + row.quantity
      // pcsPerUnit 은 상품 그룹당 상수라 BE 계약(그룹당 BOX row 1개)하에선 덮어쓰기가 안전하다.
      // 서로 다른 pcsPerUnit 의 BOX row 가 중복되는 계약 위반 케이스는 마지막 값만 보존돼 총수량이 부정확할 수 있으나, 런타임 감지가 불가하다.
      group.pcsPerUnit = row.pcsPerUnit
      group.boxUnitPrice = unitPrice
    } else {
      group.pcsQuantity = (group.pcsQuantity ?? 0) + row.quantity
      group.pcsUnitPrice = unitPrice
    }
  })

  // 총수량 = PCS 환산 총량. legacy(PCS 단독) 은 boxQuantity/pcsPerUnit 가 없어 pcsQuantity 와 동일.
  return Array.from(groups.values()).map((group) => ({
    ...group,
    totalQuantity: (group.pcsQuantity ?? 0) + (group.boxQuantity ?? 0) * (group.pcsPerUnit ?? 0),
  }))
}
